import type { SeatCell, Vehicle } from "@busbud/int-schemas/udm";

import { getSeatById, hasSeatSelection } from "@app/helpers/udm/vehicle";
import type { NapiV2CartSegment } from "@app/types/napi-v2-cart-types";
import type { NapiV2SelectedSeat } from "@app/types/napi-v2-passengers-types";

import type { SeatSelectionV2PassengerInput } from "../components/seat-selection-v2/seat-selection-form-control";

export const getPassengerSeats = (
  passenger_selected_seats: NapiV2SelectedSeat[] | null,
  passenger_index: number,
  leg_first_segment_ids: string[],
  is_roundtrip: boolean,
  trip_segments: NapiV2CartSegment[]
): SeatSelectionV2PassengerInput[] => {
  if (!Array.isArray(passenger_selected_seats)) {
    const initial_seats: SeatSelectionV2PassengerInput[] = [];

    // Previous cart isn't cleared when selecting a new departure, causing segments to be briefly undefined.
    const outbound_segment = trip_segments.find(
      segment => segment.id === leg_first_segment_ids[0]
    );

    const has_outbound_seat_selection = hasSeatSelection(
      outbound_segment?.vehicle as Vehicle
    );

    // At checkout level it's okay to do a falsy check. Some carts which doesn't have seat selection should have `null`, but it's coming as undefined.
    if (has_outbound_seat_selection) {
      initial_seats.push(
        buildInitialEmptySeat(
          passenger_index,
          leg_first_segment_ids[0]!,
          "outbound"
        )
      );
    }

    if (is_roundtrip) {
      const roundtrip_segment = trip_segments.find(
        segment => segment.id === leg_first_segment_ids[1]
      );

      const has_return_seat_selection = hasSeatSelection(
        roundtrip_segment?.vehicle as Vehicle
      );

      if (has_return_seat_selection) {
        initial_seats.push(
          buildInitialEmptySeat(
            passenger_index,
            leg_first_segment_ids[1]!,
            "return"
          )
        );
      }
    }

    return initial_seats;
  }

  return leg_first_segment_ids.reduce<SeatSelectionV2PassengerInput[]>(
    (result, segment_id, index) => {
      const vehicle =
        trip_segments &&
        trip_segments.find(
          segment => segment.id === leg_first_segment_ids[index]
        )?.vehicle;

      const has_seat_selection = hasSeatSelection(
        vehicle as Vehicle<SeatCell> | undefined
      );
      if (has_seat_selection) {
        const seat_id =
          passenger_selected_seats.find(seat => seat.segment_id === segment_id)
            ?.seat_id || "";
        const direction = index === 0 ? "outbound" : "return";
        const segment_id_safe = leg_first_segment_ids[index] || "";
        const display_name =
          (vehicle &&
            getSeatById(vehicle as Vehicle<SeatCell>, seat_id)?.display_name) ||
          "";

        result.push(
          buildSeat(
            passenger_index,
            segment_id_safe,
            direction,
            seat_id,
            display_name
          )
        );
      }
      return result;
    },
    []
  );
};

export function buildInitialEmptySeat(
  passenger_index: number,
  segment_id: string,
  direction: "outbound" | "return"
): SeatSelectionV2PassengerInput {
  return buildSeat(passenger_index, segment_id, direction, "", "");
}

export function buildSeat(
  passenger_index: number,
  segment_id: string,
  direction: "outbound" | "return",
  seat_id: string,
  display_name: string
): SeatSelectionV2PassengerInput {
  return {
    index: passenger_index,
    segment_id,
    direction,
    value: seat_id,
    display_name
  };
}
